import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { SEO } from "../components/SEO";
import {
  CheckCircle2, AlertCircle, Clock, MessageSquare, Send,
  Shirt, Palette, MapPin, Layers, Hash, Ruler, ChevronDown,
  Copy, Check, Gift
} from "lucide-react";
import { API, GARMENT_TYPES } from "../constants";

const SHAPES = {
  tshirt: "M 25,18 Q 22,18 15,22 L 8,35 L 18,38 L 22,28 L 22,78 Q 22,82 28,82 L 72,82 Q 78,82 78,78 L 78,28 L 82,38 L 92,35 L 85,22 Q 78,18 75,18 Q 70,18 62,16 Q 56,14 50,14 Q 44,14 38,16 Q 30,18 25,18 Z",
  hoodie: "M 22,20 Q 18,20 10,24 L 2,40 L 14,44 L 18,30 L 18,82 Q 18,86 24,86 L 76,86 Q 82,86 82,82 L 82,30 L 86,44 L 98,40 L 90,24 Q 82,20 78,20 Q 72,20 64,18 L 62,12 Q 56,8 50,8 Q 44,8 38,12 L 36,18 Q 28,20 22,20 Z",
  tank: "M 30,16 L 28,18 L 22,78 Q 22,82 28,82 L 72,82 Q 78,82 78,78 L 72,18 L 70,16 Q 64,14 58,12 Q 54,11 50,11 Q 46,11 42,12 Q 36,14 30,16 Z",
  longsleeve: "M 25,18 Q 22,18 15,22 L 4,55 L 14,58 L 22,30 L 22,78 Q 22,82 28,82 L 72,82 Q 78,82 78,78 L 78,30 L 86,58 L 96,55 L 85,22 Q 78,18 75,18 Q 70,18 62,16 Q 56,14 50,14 Q 44,14 38,16 Q 30,18 25,18 Z",
  polo: "M 25,18 Q 22,18 15,22 L 8,35 L 18,38 L 22,28 L 22,78 Q 22,82 28,82 L 72,82 Q 78,82 78,78 L 78,28 L 82,38 L 92,35 L 85,22 Q 78,18 75,18 Q 70,18 62,16 Q 56,14 50,14 Q 44,14 38,16 Q 30,18 25,18 Z",
  crewneck: "M 25,18 Q 22,18 15,22 L 4,50 L 14,53 L 22,30 L 22,82 Q 22,86 28,86 L 72,86 Q 78,86 78,82 L 78,30 L 86,53 L 96,50 L 85,22 Q 78,18 75,18 Q 70,18 62,16 Q 56,14 50,14 Q 44,14 38,16 Q 30,18 25,18 Z",
  hat: "M 15,55 Q 15,35 30,25 Q 40,18 50,18 Q 60,18 70,25 Q 85,35 85,55 L 85,60 Q 50,62 15,60 Z",
};

const COLORS_MAP = {
  "#FFFFFF": "White", "#1a1a1a": "Black", "#1e3a5f": "Navy", "#c0392b": "Red",
  "#2980b9": "Royal Blue", "#27ae60": "Forest", "#444444": "Charcoal",
  "#95a5a6": "Gray", "#e67e22": "Orange", "#7b241c": "Maroon",
  "#6c3483": "Purple", "#d4ac0d": "Gold", "#e91e8a": "Pink",
};

const PLACEMENTS = {
  front: { label: "Front", x: 50, y: 42, w: 42, h: 38 },
  back: { label: "Back", x: 50, y: 42, w: 42, h: 38 },
  left_chest: { label: "Left Chest", x: 63, y: 28, w: 16, h: 14 },
  right_chest: { label: "Right Chest", x: 37, y: 28, w: 16, h: 14 },
};

const STATUS_CONFIG = {
  pending: { label: "Pending Review", color: "bg-amber-100 text-amber-800", icon: Clock },
  approved: { label: "Approved", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  changes_requested: { label: "Changes Requested", color: "bg-red-100 text-red-800", icon: AlertCircle },
};

const ProofPage = () => {
  const { proofId } = useParams();
  const [proof, setProof] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showChangeForm, setShowChangeForm] = useState(false);
  const [responding, setResponding] = useState(false);
  const [responseForm, setResponseForm] = useState({ name: "", email: "", comment: "" });
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [gettingReferral, setGettingReferral] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [commentName, setCommentName] = useState("");
  const [addingComment, setAddingComment] = useState(false);

  useEffect(() => {
    const fetchProof = async () => {
      try {
        const { data } = await axios.get(`${API}/proofs/${proofId}`);
        setProof(data);
      } catch (err) {
        setError(err.response?.status === 404 ? "Proof not found" : "Failed to load proof");
      } finally {
        setLoading(false);
      }
    };
    fetchProof();
  }, [proofId]);

  const handleRespond = async (action) => {
    setResponding(true);
    try {
      const { data } = await axios.post(`${API}/proofs/${proofId}/respond`, {
        action,
        name: responseForm.name,
        email: responseForm.email,
        comment: responseForm.comment,
      });
      setProof(prev => ({ ...prev, status: data.status, responses: [...(prev.responses || []), data.response] }));
      setSubmitted(true);
      setShowChangeForm(false);
    } catch {
      alert("Failed to submit response. Please try again.");
    } finally {
      setResponding(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getReferralCode = async () => {
    setGettingReferral(true);
    try {
      const { data } = await axios.post(`${API}/coupons/generate-referral`, { source: `proof-${proofId}` });
      setReferralCode(data.code);
    } catch {
      setReferralCode("NEWCUSTOMER25");
    } finally {
      setGettingReferral(false);
    }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    setAddingComment(true);
    try {
      const { data } = await axios.post(`${API}/proofs/${proofId}/comment`, {
        name: commentName || "Anonymous",
        comment: newComment.trim(),
      });
      setProof(prev => ({ ...prev, responses: [...(prev.responses || []), data.comment] }));
      setNewComment("");
    } catch {
      alert("Failed to add comment");
    } finally {
      setAddingComment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading proof...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-gray-900 mb-1">Proof Not Found</h2>
            <p className="text-sm text-gray-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const design = proof?.design || {};
  const garmentType = design.garment_type || "tshirt";
  const garmentInfo = GARMENT_TYPES[garmentType] || { name: garmentType, price: 0 };
  const colorHex = design.garment_color
    ? Object.entries(COLORS_MAP).find(([, name]) => name.toLowerCase() === design.garment_color?.toLowerCase())?.[0]
      || design.garment_color
    : "#FFFFFF";
  const colorName = COLORS_MAP[colorHex] || design.garment_color || "White";
  const isDark = ["#000000", "#1a1a1a", "#1e3a5f", "#444444", "#7b241c", "#6c3483"].includes(colorHex?.toLowerCase());
  const pl = PLACEMENTS[design.placement] || PLACEMENTS.front;
  const statusCfg = STATUS_CONFIG[proof.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusCfg.icon;
  const artworkUrl = design.artwork_url || "";

  return (
    <div className="min-h-screen bg-paper" data-testid="proof-page">
      <SEO title={`Proof ${proof.proof_id}`} description="Review and approve your design proof" />

      {/* Header */}
      <header className="bg-bone border-b-2 border-ink">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-archivo text-lg uppercase tracking-tight text-ink" data-testid="proof-title">Design Proof</h1>
            <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted">{proof.proof_id} &middot; {proof.design_number}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`font-mono text-[10px] uppercase tracking-[0.1em] font-bold px-3 py-1 border-2 border-ink ${proof.status === "approved" ? "bg-acid text-ink" : proof.status === "changes_requested" ? "bg-signal text-bone" : "bg-stock text-ink"}`} data-testid="proof-status">
              <StatusIcon className="w-3.5 h-3.5 inline mr-1" />
              {statusCfg.label}
            </span>
            <button onClick={copyLink} className="font-mono text-[10px] uppercase tracking-[0.1em] font-bold px-3 py-1.5 border-2 border-ink bg-bone text-ink hover:bg-ink hover:text-acid transition-colors" data-testid="copy-link-btn">
              {copied ? <Check className="w-3.5 h-3.5 inline mr-1" /> : <Copy className="w-3.5 h-3.5 inline mr-1" />}
              {copied ? "Copied!" : "Copy Link"}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="grid md:grid-cols-2 gap-6">

          {/* Left: Mockup Preview */}
          <div>
            <Card className="border-0 shadow-md overflow-hidden">
              <CardContent className="p-0">
                <div
                  className="flex items-center justify-center relative"
                  style={{
                    background: "linear-gradient(145deg, #f1f5f9 0%, #e2e8f0 100%)",
                    minHeight: 360,
                  }}
                  data-testid="proof-mockup"
                >
                  <svg viewBox="0 0 100 100" className="w-full max-w-[320px] p-6" style={{ filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.1))" }}>
                    <path d={SHAPES[garmentType] || SHAPES.tshirt} fill={colorHex} stroke={isDark ? "#555" : "#ddd"} strokeWidth="0.5" />
                    {artworkUrl && (
                      <image
                        href={artworkUrl}
                        x={pl.x - pl.w / 2}
                        y={pl.y - pl.h / 2}
                        width={pl.w}
                        height={pl.h}
                        preserveAspectRatio="xMidYMid meet"
                      />
                    )}
                    {!artworkUrl && (
                      <text x={pl.x} y={pl.y + 1} textAnchor="middle" fill={isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.08)"} fontSize="3">
                        Your Design
                      </text>
                    )}
                  </svg>
                  <div className="absolute bottom-3 left-3">
                    <Badge variant="secondary" className="text-[10px] bg-white/80 backdrop-blur-sm">
                      {pl.label} Placement
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Message from sender */}
            {proof.message && (
              <Card className="border-0 shadow-sm mt-4">
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    <MessageSquare className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-gray-800 mb-1">Message from Fit Print Bros</p>
                      <p className="text-sm text-gray-600 italic">"{proof.message}"</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right: Specs + Actions */}
          <div className="space-y-4">
            {/* Order Specs */}
            <Card className="border-0 shadow-md">
              <CardContent className="p-5">
                <h2 className="text-sm font-bold text-gray-900 mb-4" data-testid="proof-specs-title">Order Specifications</h2>
                <div className="space-y-3">
                  <SpecRow icon={Shirt} label="Product" value={design.product_name || garmentInfo.name} testId="spec-product" />
                  {design.garment_type && (
                    <SpecRow icon={Shirt} label="Garment" value={garmentInfo.name} testId="spec-garment" />
                  )}
                  {design.garment_color && (
                    <SpecRow icon={Palette} label="Color" value={colorName} testId="spec-color"
                      extra={<div className="w-5 h-5 rounded-full border-2 border-gray-200" style={{ backgroundColor: colorHex }} />}
                    />
                  )}
                  <SpecRow icon={MapPin} label="Placement" value={pl.label} testId="spec-placement" />
                  {design.print_method && (
                    <SpecRow icon={Layers} label="Print Method" value={design.print_method.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())} testId="spec-method" />
                  )}
                  {design.size && (
                    <SpecRow icon={Ruler} label="Size" value={design.size} testId="spec-size" />
                  )}
                  <SpecRow icon={Hash} label="Quantity" value={`${design.quantity || 0} pcs`} testId="spec-quantity" />
                  {design.notes && (
                    <div className="pt-2 border-t">
                      <p className="text-[11px] text-gray-500 mb-1">Notes</p>
                      <p className="text-sm text-gray-700">{design.notes}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            {proof.status === "pending" && !submitted && (
              <Card className="border-0 shadow-md" data-testid="proof-actions">
                <CardContent className="p-5">
                  <h3 className="text-sm font-bold text-gray-900 mb-1">Your Approval</h3>
                  <p className="text-xs text-gray-500 mb-4">Review the mockup and specs above, then approve or request changes.</p>

                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        placeholder="Your name"
                        value={responseForm.name}
                        onChange={(e) => setResponseForm(f => ({ ...f, name: e.target.value }))}
                        className="h-9 text-sm"
                        data-testid="response-name"
                      />
                      <Input
                        placeholder="Your email"
                        type="email"
                        value={responseForm.email}
                        onChange={(e) => setResponseForm(f => ({ ...f, email: e.target.value }))}
                        className="h-9 text-sm"
                        data-testid="response-email"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        onClick={() => handleRespond("approved")}
                        disabled={responding}
                        className="bg-green-600 hover:bg-green-700 text-white font-bold h-11"
                        data-testid="approve-btn"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1.5" />
                        {responding ? "Submitting..." : "Approve"}
                      </Button>
                      <Button
                        onClick={() => setShowChangeForm(!showChangeForm)}
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50 font-bold h-11"
                        data-testid="request-changes-btn"
                      >
                        <AlertCircle className="w-4 h-4 mr-1.5" />
                        Request Changes
                        <ChevronDown className={`w-3.5 h-3.5 ml-1 transition-transform ${showChangeForm ? "rotate-180" : ""}`} />
                      </Button>
                    </div>

                    {showChangeForm && (
                      <div className="space-y-2 pt-2 border-t" data-testid="change-form">
                        <Textarea
                          placeholder="Describe the changes you'd like (e.g., 'Make the logo bigger', 'Change font to bold')"
                          value={responseForm.comment}
                          onChange={(e) => setResponseForm(f => ({ ...f, comment: e.target.value }))}
                          className="text-sm min-h-[80px]"
                          data-testid="change-comment"
                        />
                        <Button
                          onClick={() => handleRespond("changes_requested")}
                          disabled={responding || !responseForm.comment.trim()}
                          className="w-full bg-red-600 hover:bg-red-700 text-white"
                          data-testid="submit-changes-btn"
                        >
                          <Send className="w-4 h-4 mr-1.5" />
                          {responding ? "Submitting..." : "Submit Change Request"}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Success state */}
            {submitted && (
              <Card className="border-0 shadow-md border-l-4 border-l-green-500" data-testid="proof-submitted">
                <CardContent className="p-5 text-center">
                  <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  <h3 className="text-base font-bold text-gray-900 mb-1">
                    {proof.status === "approved" ? "Proof Approved!" : "Changes Requested"}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {proof.status === "approved"
                      ? "Thank you! The Fit Print Bros team has been notified and will begin production."
                      : "Thank you! The team has been notified and will update your design."}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Already responded state */}
            {proof.status !== "pending" && !submitted && (
              <Card className="border-0 shadow-md" data-testid="proof-already-responded">
                <CardContent className="p-5 text-center">
                  <StatusIcon className={`w-10 h-10 mx-auto mb-2 ${proof.status === "approved" ? "text-green-500" : "text-red-500"}`} />
                  <h3 className="text-base font-bold text-gray-900 mb-1">
                    {proof.status === "approved" ? "This Proof Was Approved" : "Changes Were Requested"}
                  </h3>
                </CardContent>
              </Card>
            )}

            {/* Collaborative Comment Thread */}
            <Card className="border-0 shadow-md" data-testid="comment-thread">
              <CardContent className="p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-1.5">
                  <MessageSquare className="w-4 h-4 text-orange-500" /> Discussion
                  {proof.responses?.length > 0 && <Badge variant="secondary" className="text-[10px]">{proof.responses.length}</Badge>}
                </h3>

                {/* Existing responses */}
                {proof.responses?.length > 0 ? (
                  <div className="space-y-2 mb-4 max-h-[300px] overflow-y-auto">
                    {proof.responses.map((r) => (
                      <div key={r.id} className="bg-gray-50 rounded-lg p-3 text-sm">
                        <div className="flex items-center gap-2 mb-1">
                          {r.action === "approved" ? (
                            <Badge className="bg-green-100 text-green-700 text-[10px]">Approved</Badge>
                          ) : r.action === "changes_requested" ? (
                            <Badge className="bg-red-100 text-red-700 text-[10px]">Changes Requested</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-700 text-[10px]">Comment</Badge>
                          )}
                          <span className="text-xs font-medium text-gray-700">{r.name || "Anonymous"}</span>
                          <span className="text-[10px] text-gray-400">{new Date(r.timestamp).toLocaleString()}</span>
                        </div>
                        {r.comment && <p className="text-gray-600 text-sm">{r.comment}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 mb-4">No comments yet. Start the conversation!</p>
                )}

                {/* Add comment form */}
                <div className="border-t pt-3 space-y-2" data-testid="add-comment-form">
                  <div className="flex gap-2">
                    <Input
                      value={commentName}
                      onChange={(e) => setCommentName(e.target.value)}
                      placeholder="Your name"
                      className="w-32 h-8 text-xs"
                      data-testid="comment-name-input"
                    />
                    <Input
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      className="flex-1 h-8 text-xs"
                      data-testid="comment-input"
                      onKeyDown={(e) => e.key === "Enter" && addComment()}
                    />
                    <Button
                      size="sm"
                      onClick={addComment}
                      disabled={addingComment || !newComment.trim()}
                      className="h-8 text-xs bg-orange-600 hover:bg-orange-700"
                      data-testid="submit-comment-btn"
                    >
                      <Send className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Referral CTA */}
        <Card className="border-0 shadow-md mt-6 bg-gradient-to-r from-orange-50 to-amber-50 overflow-hidden" data-testid="referral-cta">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                <Gift className="w-5 h-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-gray-900">Need custom printing?</h3>
                <p className="text-xs text-gray-600 mt-0.5">Get $25 off your first order with Fit Print Bros — custom apparel, stickers, banners & more.</p>
                {referralCode ? (
                  <div className="mt-3 bg-white border-2 border-dashed border-orange-300 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-gray-500 mb-1">Your promo code:</p>
                    <p className="text-lg font-black text-orange-600 tracking-widest" data-testid="referral-code">{referralCode}</p>
                    <p className="text-[10px] text-gray-400 mt-1">$25 off orders $50+ &middot; New customers only</p>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    onClick={getReferralCode}
                    disabled={gettingReferral}
                    className="mt-2 bg-gradient-to-r from-orange-500 to-red-600 text-white text-xs h-8"
                    data-testid="get-referral-btn"
                  >
                    {gettingReferral ? "Generating..." : "Get $25 Off Code"}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 py-4 border-t">
          <p className="text-xs text-gray-400">
            Powered by <a href="/" className="text-orange-500 hover:text-orange-600 font-medium">Fit Print Bros</a> &mdash; Professional Custom Printing
          </p>
        </div>
      </div>
    </div>
  );
};

/* ── Spec Row Component ── */
const SpecRow = ({ icon: Icon, label, value, extra, testId }) => (
  <div className="flex items-center justify-between" data-testid={testId}>
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-gray-400" />
      <span className="text-xs text-gray-500">{label}</span>
    </div>
    <div className="flex items-center gap-2">
      {extra}
      <span className="text-sm font-medium text-gray-900">{value}</span>
    </div>
  </div>
);

export default ProofPage;
